import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Play, Zap } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function PlatformDigest() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const runAll = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/platform/run-weekly-digest");
      toast.success(t("platform.digest.dispatched", { count: data.tenants }));
      setLastRun({
        at: new Date().toISOString(),
        tenants: data.tenants,
        results: data.results,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="platform-digest">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">Serpix Admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("platform.nav.digest")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {t("platform.digest.subtitle")}
        </p>
      </header>

      <div
        className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-indigo-50 to-fuchsia-50 p-6"
        data-testid="platform-digest-card"
      >
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-md">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-950">
              {t("platform.digest.weekly_title")}
            </h3>
            <p className="text-xs text-zinc-600">{t("platform.digest.weekly_desc")}</p>
          </div>
        </div>
        <Button
          onClick={runAll}
          disabled={running}
          data-testid="platform-digest-run-all"
          className="mt-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/20 hover:from-violet-700 hover:to-indigo-700"
        >
          {running ? (
            <>
              <Zap className="mr-1.5 h-4 w-4 animate-pulse" /> {t("platform.digest.running")}
            </>
          ) : (
            <>
              <Play className="mr-1.5 h-4 w-4" /> {t("platform.digest.run_all")}
            </>
          )}
        </Button>
      </div>

      {lastRun && (
        <div
          className="rounded-xl border border-zinc-200 bg-white p-5"
          data-testid="platform-digest-last-run"
        >
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-semibold text-zinc-900">
              {t("platform.digest.last_run")}
            </h4>
            <span className="text-[11px] text-zinc-500">
              {new Date(lastRun.at).toLocaleString()}
            </span>
          </div>
          <div className="mb-2 text-sm text-zinc-600">
            {t("platform.digest.dispatched", { count: lastRun.tenants })}
          </div>
          {Array.isArray(lastRun.results) && lastRun.results.length > 0 && (
            <details>
              <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {t("platform.digest.result_details")}
              </summary>
              <pre className="mt-2 max-h-80 overflow-auto rounded bg-zinc-50 p-3 font-mono text-[10px] text-zinc-700">
                {JSON.stringify(lastRun.results, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
